import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PrintingJourney from './PrintingJourney';
import './App.css';

gsap.registerPlugin(ScrollTrigger);

const RESEARCH_PORTAL_URL = process.env.REACT_APP_RESEARCH_PORTAL_URL || '/research/';
const SMART_ENGINE_URL = 'https://engine.matic-3d.com/engine';
const DEMO_WHATSAPP_URL = `https://wa.me/9647810112266?text=${encodeURIComponent('مرحباً، أرغب في الحصول على عرض تجريبي من MATIC.')}`;

function App() {
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const framesRef = useRef([]);
  const heroStageRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isArabic, setIsArabic] = useState(false);
  const [navVisible, setNavVisible] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = heroStageRef.current;
    if (!canvas || !stage) return undefined;
    const context = canvas.getContext('2d');
    let cancelled = false;

    const paint = (frame) => {
      const scale = Math.max(canvas.width / frame.width, canvas.height / frame.height)
        * (window.innerWidth > 800 ? 1.03 : 1);
      const width = frame.width * scale;
      const height = frame.height * scale;
      const verticalOffset = window.innerWidth > 800 ? canvas.height * 0.04 : 0;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(frame, (canvas.width - width) / 2, (canvas.height - height) / 2 - verticalOffset, width, height);
    };

    const extractFrames = async () => {
      const frames = await Promise.all(Array.from({ length: 41 }, (_, index) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = `/matic-frames/frame-${String(index + 1).padStart(3, '0')}.jpg`;
      })));
      if (!cancelled) {
        framesRef.current = frames;
        paint(frames[0]);
        const trigger = ScrollTrigger.create({
          trigger: stage,
          start: 'top top',
          end: 'bottom top',
          pin: canvas.parentElement.parentElement,
          pinSpacing: false,
          scrub: 0.35,
          anticipatePin: 1,
          onUpdate: ({ progress }) => {
            const frame = frames[Math.min(frames.length - 1, Math.floor(progress * frames.length))];
            paint(frame);
            gsap.set(canvas, { scale: 1 + progress * 0.06 });
            setNavVisible(progress >= 0.98);
          },
        });
        canvas.dataset.trigger = String(trigger.id);
      }
    };

    const resize = () => {
      canvas.width = canvas.clientWidth * window.devicePixelRatio;
      canvas.height = canvas.clientHeight * window.devicePixelRatio;
      if (framesRef.current[0]) paint(framesRef.current[0]);
    };
    resize();
    window.addEventListener('resize', resize);
    extractFrames();
    return () => {
      cancelled = true;
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger === stage) trigger.kill();
      });
      window.removeEventListener('resize', resize);
      framesRef.current = [];
    };
  }, []);

  const addFiles = (files) => {
    const nextFiles = Array.from(files)
      .filter((file) => file.name.toLowerCase().endsWith('.stl'))
      .map((file) => ({
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        type: 'STL Model',
      }));
    if (nextFiles.length > 0) {
      window.location.assign(SMART_ENGINE_URL);
      return;
    }
    setUploadedFiles((currentFiles) => [...currentFiles, ...nextFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  };

  return (
    <div className="app" id="top">
      <section className="hero-video-scroll-space" ref={heroStageRef}>
        <div className="hero-video-viewport">
          <div className="hero-bg">
            <canvas
              className="hero-video-canvas"
              ref={canvasRef}
            />
            <video
              className="hero-mobile-video"
              src="/matic-mobile-hero.mp4"
              muted
              loop
              autoPlay
              playsInline
              preload="metadata"
              aria-label="MATIC dental printer"
            />
          </div>
        </div>
      </section>

      <nav className={`page-nav${isArabic ? ' page-nav-arabic' : ''}${navVisible ? ' page-nav-visible' : ''}`} aria-label="Main navigation">
        <a href="#about">{isArabic ? 'من نحن' : 'About'}</a>
        <a href="#printing-journey">{isArabic ? 'المراحل' : 'Process'}</a>
        <a href={RESEARCH_PORTAL_URL} target="_blank" rel="noreferrer">{isArabic ? 'البحث' : 'Research Portal'}</a>
        <a href="#specifications">{isArabic ? 'المواصفات' : 'Specs'}</a>
        <a href="#contact">{isArabic ? 'تواصل' : 'Contact'}</a>
        <button className="language-toggle" type="button" onClick={() => setIsArabic((value) => !value)}>{isArabic ? 'EN' : 'عربي'}</button>
      </nav>

      <section className="hero-content-section">
        <div className="hero-content">
          <div className="hero-text-group">
            <h1 className="hero-title">{isArabic ? 'طباعة الأسنان' : 'Dental Printing'}</h1>
            <p className="hero-subtitle">{isArabic ? 'دقة عالية. نتائج موثوقة.' : 'Tiny layers. Big confidence. Laboratory-grade accuracy with micron-level precision and instant turnaround.'}</p>
            <div className="hero-cta-group">
              <a className="btn btn-accent" href="#specifications">{isArabic ? 'المواصفات' : 'See Specifications'}</a>
              <a className="btn btn-secondary" href={DEMO_WHATSAPP_URL} target="_blank" rel="noreferrer">{isArabic ? 'اطلب عرضاً' : 'Request Demo'}</a>
            </div>
          </div>
        </div>
      </section>

      <div className="matic-marquee" aria-label="MATIC, proudly made in Iraq">
        <div className="matic-marquee-track">
          {[0, 1].map((copy) => <div className="matic-marquee-copy" key={copy} aria-hidden={copy === 1}>
            <img src="/matic-logo-only.svg" alt="" />
            <strong>MATIC</strong><span>3D PRINTERS</span><i>•</i>
            <span>PROUDLY MADE IN IRAQ</span><i>•</i>
            <strong>MATIC</strong><span>DENTAL PRECISION</span><i>•</i>
            <span>PRINT SMART. PRINT LOCAL.</span><i>•</i>
            <strong>MATIC</strong><span>3D PRINTERS</span><i>•</i>
            <span>PROUDLY MADE IN IRAQ</span><i>•</i>
            <strong>MATIC</strong><span>DENTAL PRECISION</span><i>•</i>
            <span>PRINT SMART. PRINT LOCAL.</span><i>•</i>
          </div>)}
        </div>
      </div>

      <main className="main-content">
        <section className="section-container about-section" id="about">
          <div className="about-grid">
          <div className="section-header"><h2>{isArabic ? 'طباعة أسنان أفضل' : 'Better Dental Printing'}</h2></div>
            <div className="about-copy"><p>{isArabic ? 'تقدم MATIC طباعة ثلاثية الأبعاد دقيقة لعيادات الأسنان الحديثة، مع أجهزة موثوقة وسير عمل واضح.' : 'MATIC brings precision 3D printing into the daily rhythm of modern dental practices. We pair dependable hardware with an intuitive workflow, helping teams create accurate results without slowing down.'}</p></div>
          </div>
        </section>

        <PrintingJourney
          isArabic={isArabic}
          videoUrls={[
            '/printing-journey/design.mkv',
            '/printing-journey/printing.mkv',
            '/printing-journey/cleaning.mp4',
            '/printing-journey/curing.mkv',
          ]}
          stepDescriptions={[
            'Shape every dental model with a clear, precise design workflow.',
            'Build precise dental models layer by layer.',
            'Clean and prepare every result for the next step.',
            'Cure each print for a durable, stable finish.',
          ]}
        />

        <section className="section-container">
          <div className="section-header"><h2>{isArabic ? 'نتائج دقيقة' : 'Serious Results'}</h2></div>
          <div className="cards-grid">
            {(isArabic ? [
              ['01', 'طباعة سريعة', 'نماذج دقيقة في دقائق.', 'أكثر من 60 طباعة يومياً'],
              ['02', 'دقة ميكرونية', 'تفاصيل سريرية واضحة.', 'دقة ±25 ميكرون'],
              ['03', 'مواد متنوعة', 'توافق مع مختلف الراتنجات.', 'أكثر من 15 مادة'],
              ['04', 'تشغيل سهل', 'معايرة تلقائية وإعداد سريع.', 'إعداد خلال 30 ثانية'],
            ] : [
              ['01', 'Lightning-Fast Prints', 'Complete crown and bridge models in minutes, not hours.', '60+ prints/day'],
              ['02', 'Micron-Level Precision', '25-micron layer resolution captures every clinical detail.', '±25 µm accuracy'],
              ['03', 'Universal Resin Compatibility', 'Use the material that best suits each application.', '15+ resins'],
              ['04', 'Intuitive Operation', 'Automated calibration gets your team printing on day one.', '30-second setup'],
            ]).map(([number, title, , stat]) => <article className="capability-card" key={number}><h3>{title}</h3><strong>{stat}</strong></article>)}
          </div>
        </section>

        <section className="section-container queue-section">
          <div className="section-header"><h2>{isArabic ? 'سير عمل ذكي' : 'Smart Print Workflow'}</h2></div>
          <div className="grouped-card">
            <div className={`file-upload${dragActive ? ' active' : ''}`} onClick={() => fileInputRef.current?.click()} onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragActive(false)} onDrop={handleDrop}>
              <div className="upload-icon">+</div><h4>{isArabic ? 'أفلت ملفات STL لإضافتها' : 'Drop STL files to add to queue'}</h4>
              <input ref={fileInputRef} type="file" multiple accept=".stl" onChange={(event) => addFiles(event.target.files)} />
            </div>
            <div className="smart-engine-action"><a className="btn btn-smart-engine" href={SMART_ENGINE_URL} target="_blank" rel="noreferrer">{isArabic ? 'جرّب المحرك الذكي' : 'MATIC Smart Engine'}</a><a className="btn btn-research-portal" href={RESEARCH_PORTAL_URL} target="_blank" rel="noreferrer">{isArabic ? 'بوابة البحث' : 'Research Portal'}</a></div>
            {uploadedFiles.length > 0 && <div className="file-list"><div className="file-list-header"><h5>{isArabic ? `قائمة الانتظار (${uploadedFiles.length})` : `Queue (${uploadedFiles.length})`}</h5></div>{uploadedFiles.map((file, index) => <div className="file-item" key={`${file.name}-${index}`}><div><p className="file-name">{file.name}</p></div><button className="remove-btn" onClick={() => setUploadedFiles((files) => files.filter((_, i) => i !== index))} aria-label={`${isArabic ? 'إزالة' : 'Remove'} ${file.name}`}>×</button></div>)}<button className="btn btn-accent" onClick={() => setUploadedFiles([])}>{isArabic ? 'مسح القائمة' : 'Clear Queue'}</button></div>}
          </div>
        </section>

        <section className="section-container" id="specifications">
            <div className="section-header"><h2>{isArabic ? 'المواصفات التقنية' : 'Technical Specs'}</h2></div>
            <div className="specs-grid">{(isArabic ? [['الحجم والوزن', ['حجم الطابعة|24 × 24 × 25 سم', 'حجم مزود الطاقة|15 × 30 × 15 سم', 'وزن الطابعة|5 كغم', 'وزن مزود الطاقة|1.8 كغم']], ['الأداء والدقة', ['السرعة|80–100 مم/ساعة، الأسرع في فئتها', 'دقة المحور Z|حتى 10 ميكرون', 'استهلاك الطاقة|400 واط', 'حجم منصة الطباعة|160 × 85 مم']], ['البرمجيات والاتصال', ['التقطيع|MATIC On-board Slicing بدون برامج خارجية', 'الاتصال|Wi-Fi وEthernet', 'التصنيع|صناعة وطنية عراقية 100%', 'الدعم|دعم وصيانة محلية فورية وقطع غيار متوفرة']]] : [['Size & weight', ['Printer size|24 × 24 × 25 cm', 'Power supply size|15 × 30 × 15 cm', 'Printer weight|5 kg', 'Power supply weight|1.8 kg']], ['Performance & precision', ['Speed|80–100 mm/hour, fastest in its class', 'Z-axis resolution|Up to 10 micron', 'Power consumption|400 Watt', 'Print platform size|160 × 85 mm']], ['Software & connectivity', ['Slicing|MATIC On-board Slicing, no external software', 'Connectivity|Wi-Fi / Ethernet', 'Manufacturing|100% Iraqi-made', 'Support|Immediate local support, service, and spare parts']]]).map(([title, rows]) => <div className="spec-group" key={title}><h3>{title}</h3><div className="grouped-card">{rows.map((row) => { const [label, value] = row.split('|'); return <div className="spec-row" key={label}><span>{label}</span><strong>{value}</strong></div>; })}</div></div>)}</div>
        </section>

        <section className="cta-section" id="contact"><h2>{isArabic ? 'طوّر سير عملك' : 'Upgrade Your Workflow'}</h2><div className="contact-actions"><a className="btn btn-accent" href={DEMO_WHATSAPP_URL} target="_blank" rel="noreferrer">{isArabic ? 'اطلب عرضاً' : 'Request a Demo'}</a><a className="btn whatsapp-btn" href="https://wa.me/9647810112266" target="_blank" rel="noreferrer">{isArabic ? 'واتساب' : 'WhatsApp Us'}</a></div></section>
      </main>
      <footer className={`footer${isArabic ? ' footer-arabic' : ''}`}>
        <div className="footer-brand"><img src="/matic-logo.svg" alt="MATIC" /><strong>MATIC</strong></div>
        <div className="footer-meta">
          <span>@matic 2025</span>
          <span>Proudly Made in Iraq</span>
        </div>
        <div className="footer-socials">
          <a href="https://www.instagram.com/matic.iq?igsi=cnI5ODBkaGo0NDJw" target="_blank" rel="noreferrer" aria-label="MATIC on Instagram">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" className="social-dot" /></svg>
          </a>
          <a href="https://wa.me/9647810112266" target="_blank" rel="noreferrer" aria-label="MATIC on WhatsApp">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.6 0 .3 5.3.3 11.8c0 2.1.6 4.2 1.7 6L.2 24l6.3-1.7a11.8 11.8 0 0 0 5.6 1.4h.1c6.5 0 11.8-5.3 11.8-11.8 0-3.2-1.2-6.2-3.5-8.4Zm-8.4 18.2h-.1a9.8 9.8 0 0 1-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4a9.8 9.8 0 0 1-1.5-5.3C2.2 6.4 6.6 2 12.1 2c2.6 0 5.1 1 6.9 2.9a9.7 9.7 0 0 1 2.9 6.9c0 5.5-4.4 9.9-9.8 9.9Zm5.4-7.4c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-1.6-.8-2.7-1.5-3.8-3.4-.3-.5.3-.5.8-1.7.1-.2.1-.4 0-.6-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.1 1.1-1.1 2.6s1.1 3 1.3 3.2c.2.2 2.1 3.2 5.1 4.5 1.9.8 2.7.9 3.7.8.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.2-.3-.3-.6-.5Z" /></svg>
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
